// 大きさ・CSS・表示の切りかえ・フェード・動き（段階 C の2 の 7g-2、2026-10-26。前は jQuery 3.7.1 の $(…).height()・css()・show()・fadeIn()・animate() など）。
// jQuery（Copyright OpenJS Foundation and other contributors, MIT License, https://jquery.org/license）の測り方・書き方・動きの決まりを TS に移したもの。
// 画面の組み立て（boot.ts など）が測った大きさで欄を並べるので、測り方（box-sizing・padding・border・隠れた要素の測り方）と、
// 書き方（数には px を付ける・opacity などは付けない）、動きが止まったあとの形（表示・inline の opacity を元に戻す）を jQuery と同じにしている。
// 動きの途中は jQuery と細かくは同じでなくてよい（2026-10-26 ユーザー）。速さの変わり方は swing・linear と jQuery UI の名前（easeInOutBack など）
import { Timer } from './timer.js';

export type Target = string | Element | null | undefined;
type El = HTMLElement;

/** 合う要素（文字ならセレクター、要素ならそれ。空の文字・null は何も無い＝jQuery の $("") と同じ） */
export function els(t: Target): El[] {
  if (!t) return [];
  if (typeof t === 'string') return Array.prototype.slice.call(document.querySelectorAll(t)) as El[];
  return [t as El];
}
const first = (t: Target): El | null => (!t ? null : typeof t === 'string' ? (document.querySelector(t) as El | null) : (t as El));

// ---- CSS ----

// 数のときに px を付けない CSS（jQuery の cssNumber）
const CSS_NUMBER: Record<string, true> = {
  animationIterationCount: true, aspectRatio: true, borderImageSlice: true, columnCount: true, flexGrow: true, flexShrink: true, fontWeight: true,
  gridArea: true, gridColumn: true, gridColumnEnd: true, gridColumnStart: true, gridRow: true, gridRowEnd: true, gridRowStart: true, lineHeight: true,
  opacity: true, order: true, orphans: true, scale: true, widows: true, zIndex: true, zoom: true, fillOpacity: true, floodOpacity: true,
  stopOpacity: true, strokeMiterlimit: true, strokeOpacity: true,
};
const camel = (s: string): string => s.replace(/^-ms-/, 'ms-').replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
const isCustom = (name: string): boolean => /^--/.test(name);
const rcssNum = /^(?:([+-])=|)([+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|))([a-z%]*)$/i;
const rnumnonpx = /^([+-]?(?:\d*\.|)\d+(?:[eE][+-]?\d+|))(?!px)[a-z%]+$/i;
const EXPAND = ['Top', 'Right', 'Bottom', 'Left'];

const isAttached = (el: Element): boolean => el.ownerDocument.contains(el) || (el.getRootNode && el.getRootNode({ composed: true }) === el.ownerDocument);
const stylesOf = (el: Element): CSSStyleDeclaration => (el.ownerDocument.defaultView || window).getComputedStyle(el);

/** 計算後の値（jQuery の curCSS。付いていない要素は inline の値） */
function curCSS(el: El, name: string, computed?: CSSStyleDeclaration): string {
  const cs = computed || stylesOf(el);
  let v = cs.getPropertyValue(name) || (cs as unknown as Record<string, string>)[name];
  if (isCustom(name) && v) v = v.replace(/^[\x20\t\r\n\f]+|((?:^|[^\\])[\x20\t\r\n\f]+)$/g, '$1') || '';
  if (v === '' && !isAttached(el)) v = styleOf(el, name) as string;
  return v === undefined ? (v as unknown as string) : v + '';
}

/** inline の値・計算後の値を数にして（jQuery の jQuery.css(要素, 名前, true)） */
const cssNum = (el: El, name: string, computed?: CSSStyleDeclaration): number => parseFloat(cssValue(el, name, computed)) || 0;

/** CSS の値（jQuery の $(…).css(名前)。width・height・opacity は jQuery と同じ測り方） */
function cssValue(el: El, name: string, computed?: CSSStyleDeclaration, extra?: string): string {
  const prop = isCustom(name) ? name : camel(name);
  let v: string | undefined;
  if (prop === 'opacity') {
    const o = curCSS(el, 'opacity');
    v = o === '' ? '1' : o;
  } else if (prop === 'height' || prop === 'width') v = dimHookGet(el, prop, extra);
  if (v === undefined) v = curCSS(el, prop, computed);
  if (v === 'normal' && (prop === 'letterSpacing' || prop === 'fontWeight')) v = prop === 'letterSpacing' ? '0' : '400';
  return v;
}

/** inline の値（jQuery の jQuery.style(要素, 名前)） */
function styleOf(el: El, name: string): string {
  const prop = isCustom(name) ? name : camel(name);
  return (el.style as unknown as Record<string, string>)[prop];
}

/** CSS を書く（jQuery の jQuery.style(要素, 名前, 値)。数には px（opacity などは付けない）、"+=10" は今の値から） */
function styleSet(el: El, name: string, value: unknown, extra?: string): void {
  if (!el || el.nodeType === 3 || el.nodeType === 8 || !el.style) return;
  const custom = isCustom(name);
  const prop = custom ? name : camel(name);
  let v = value as string | number | null | undefined;
  let type = typeof v;
  let m: RegExpExecArray | null = null;
  if (type === 'string' && (m = rcssNum.exec(v as string)) && m[1]) {
    v = adjustCSS(el, prop, m);
    type = 'number';
  }
  if (v == null || v !== v) return;
  if (type === 'number' && !custom) v = v + ((m && m[3]) || (CSS_NUMBER[prop] ? '' : 'px'));
  if (prop === 'height' || prop === 'width') v = dimHookSet(el, prop, v as string, extra);
  if (custom) el.style.setProperty(prop, v as string);
  else (el.style as unknown as Record<string, unknown>)[prop] = v;
}

// "+=10px" など（jQuery の adjustCSS の、単位が同じ形だけ）
function adjustCSS(el: El, prop: string, m: RegExpExecArray): number {
  const cur = parseFloat(cssValue(el, prop, undefined, '')) || 0;
  return m[1] ? cur + (m[1] + 1 === '+1' ? 1 : -1) * +m[2] : +m[2];
}

// ---- 大きさ（jQuery の width・height・inner*・outer*） ----

type Box = 'content' | 'padding' | 'border' | 'margin';

function boxModelAdjustment(el: El, dim: 'width' | 'height', box: Box, isBorderBox: boolean, styles: CSSStyleDeclaration, computedVal?: number): number {
  let i = dim === 'width' ? 1 : 0, extra = 0, delta = 0, marginDelta = 0;
  if (box === (isBorderBox ? 'border' : 'content')) return 0;
  for (; i < 4; i += 2) {
    if (box === 'margin') marginDelta += cssNum(el, 'margin' + EXPAND[i], styles);
    if (!isBorderBox) {
      delta += cssNum(el, 'padding' + EXPAND[i], styles);
      if (box !== 'padding') delta += cssNum(el, 'border' + EXPAND[i] + 'Width', styles);
      else extra += cssNum(el, 'border' + EXPAND[i] + 'Width', styles);
    } else {
      if (box === 'content') delta -= cssNum(el, 'padding' + EXPAND[i], styles);
      if (box !== 'margin') delta -= cssNum(el, 'border' + EXPAND[i] + 'Width', styles);
    }
  }
  if (!isBorderBox && computedVal !== undefined && computedVal >= 0) {
    const offset = (el as unknown as Record<string, number>)['offset' + dim[0].toUpperCase() + dim.slice(1)];
    delta += Math.max(0, Math.ceil(offset - computedVal - delta - extra - 0.5)) || 0;
  }
  return delta + marginDelta;
}

function getWidthOrHeight(el: El, dim: 'width' | 'height', extra?: string): string {
  const styles = stylesOf(el);
  let isBorderBox = !!extra && cssValue(el, 'boxSizing', styles) === 'border-box';
  let valueIsBorderBox = isBorderBox;
  let val: string | number = curCSS(el, dim, styles);
  const offsetProp = 'offset' + dim[0].toUpperCase() + dim.slice(1);
  if (rnumnonpx.test(val)) {
    if (!extra) return val;
    val = 'auto';
  }
  if ((val === 'auto' || (!parseFloat(val) && cssValue(el, 'display', styles) === 'inline')) && el.getClientRects().length) {
    isBorderBox = cssValue(el, 'boxSizing', styles) === 'border-box';
    valueIsBorderBox = offsetProp in el;
    if (valueIsBorderBox) val = (el as unknown as Record<string, number>)[offsetProp];
  }
  const num = parseFloat(val as string) || 0;
  return num + boxModelAdjustment(el, dim, (extra || (isBorderBox ? 'border' : 'content')) as Box, valueIsBorderBox, styles, num) + 'px';
}

// display:none・table-* の要素は、見えない形で一度出して測る（jQuery の cssShow）
function dimHookGet(el: El, dim: 'width' | 'height', extra?: string): string {
  const disp = curCSS(el, 'display');
  if (!/^(none|table(?!-c[ea]).+)/.test(disp) || (el.getClientRects().length && el.getBoundingClientRect().width)) return getWidthOrHeight(el, dim, extra);
  const swap: Record<string, string> = { position: 'absolute', visibility: 'hidden', display: 'block' };
  const old: Record<string, string> = {};
  const st = el.style as unknown as Record<string, string>;
  for (const k in swap) (old[k] = st[k]), (st[k] = swap[k]);
  const r = getWidthOrHeight(el, dim, extra);
  for (const k in swap) st[k] = old[k];
  return r;
}
function dimHookSet(el: El, dim: 'width' | 'height', value: string, extra?: string): string {
  const styles = stylesOf(el);
  const isBorderBox = !!extra && cssValue(el, 'boxSizing', styles) === 'border-box';
  const subtract = extra ? boxModelAdjustment(el, dim, extra as Box, isBorderBox, styles) : 0;
  let m: RegExpExecArray | null;
  if (subtract && (m = rcssNum.exec(value)) && (m[3] || 'px') !== 'px') {
    (el.style as unknown as Record<string, string>)[dim] = value;
    value = cssValue(el, dim);
  }
  m = rcssNum.exec(value);
  return m ? Math.max(0, +m[2] - (subtract || 0)) + (m[3] || 'px') : value;
}

function dimension(t: Target | Window | Document, name: 'Height' | 'Width', box: Box): number {
  if (t === window) return box === 'border' || box === 'margin' ? (window as unknown as Record<string, number>)['inner' + name] : document.documentElement[('client' + name) as 'clientHeight'];
  if (t === document) {
    const d = document.documentElement, b = document.body as unknown as Record<string, number>, dd = d as unknown as Record<string, number>;
    return Math.max(b['scroll' + name], dd['scroll' + name], b['offset' + name], dd['offset' + name], dd['client' + name]);
  }
  const el = first(t as Target);
  if (!el) return undefined as unknown as number;
  const v = cssValue(el, name.toLowerCase(), undefined, box);
  const n = parseFloat(v);
  return isFinite(n) ? n || 0 : (v as unknown as number);
}
/** 中身の高さ（jQuery の height()。window は表示域、document は文書全体） */
export const height = (t: Target | Window | Document): number => dimension(t, 'Height', 'content');
export const width = (t: Target | Window | Document): number => dimension(t, 'Width', 'content');
/** padding まで（jQuery の innerHeight()） */
export const innerHeight = (t: Target): number => dimension(t, 'Height', 'padding');
export const innerWidth = (t: Target): number => dimension(t, 'Width', 'padding');
/** border まで（jQuery の outerHeight()。margin も入れるなら withMargin） */
export const outerHeight = (t: Target, withMargin = false): number => dimension(t, 'Height', withMargin ? 'margin' : 'border');
export const outerWidth = (t: Target, withMargin = false): number => dimension(t, 'Width', withMargin ? 'margin' : 'border');
/** 中身の高さを書く（jQuery の height(値)。数は px、undefined なら何もしない） */
export function setHeight(t: Target, value: unknown): void {
  if (value === undefined) return;
  for (const el of els(t)) styleSet(el, 'height', value, 'content');
}
export function setWidth(t: Target, value: unknown): void {
  if (value === undefined) return;
  for (const el of els(t)) styleSet(el, 'width', value, 'content');
}

/** CSS の値（jQuery の css(名前)。要素が無ければ undefined） */
export function css(t: Target, name: string): string {
  const el = first(t);
  return el ? cssValue(el, name) : (undefined as unknown as string);
}
/** CSS を書く（jQuery の css(名前, 値)・css({ 名前: 値 })。数には px（opacity などは付けない）） */
export function setCss(t: Target, name: string | Record<string, unknown>, value?: unknown): void {
  const list = els(t);
  if (typeof name === 'object') {
    for (const el of list) for (const k in name) styleSet(el, k, name[k]);
    return;
  }
  if (value === undefined) return;
  for (const el of list) styleSet(el, name, value);
}

/** 文書の上での位置（jQuery の offset()。要素が無ければ undefined、付いていなければ 0, 0） */
export function offset(t: Target): { top: number; left: number } | undefined {
  const el = first(t);
  if (!el) return undefined;
  if (!el.getClientRects().length) return { top: 0, left: 0 };
  const rect = el.getBoundingClientRect(), win = el.ownerDocument.defaultView as Window;
  return { top: rect.top + win.pageYOffset, left: rect.left + win.pageXOffset };
}

// ---- 表示の切りかえ（jQuery の show・hide・toggle） ----

const displayStore = new WeakMap<Element, string>();
const defaultDisplays: Record<string, string> = {};
const isHiddenWithinTree = (el: El): boolean => el.style.display === 'none' || (el.style.display === '' && isAttached(el) && cssValue(el, 'display') === 'none');
function defaultDisplay(el: El): string {
  const name = el.nodeName;
  let d = defaultDisplays[name];
  if (d) return d;
  const tmp = el.ownerDocument.body.appendChild(el.ownerDocument.createElement(name));
  d = cssValue(tmp as El, 'display');
  tmp.parentNode!.removeChild(tmp);
  if (d === 'none') d = 'block';
  return (defaultDisplays[name] = d);
}
function showHide(list: El[], show: boolean): void {
  const values: (string | null)[] = [];
  list.forEach((el, i) => {
    if (!el.style) return;
    const display = el.style.display;
    if (show) {
      if (display === 'none') {
        values[i] = displayStore.get(el) || null;
        if (!values[i]) el.style.display = '';
      }
      if (el.style.display === '' && isHiddenWithinTree(el)) values[i] = defaultDisplay(el);
    } else if (display !== 'none') {
      values[i] = 'none';
      displayStore.set(el, display);
    }
  });
  list.forEach((el, i) => {
    if (values[i] != null) el.style.display = values[i] as string;
  });
}
export const show = (t: Target): void => showHide(els(t), true);
export const hide = (t: Target): void => showHide(els(t), false);
export function toggle(t: Target): void {
  for (const el of els(t)) showHide([el], isHiddenWithinTree(el));
}

// ---- 動き（jQuery の animate・fadeIn・fadeOut。同じ要素の動きは順番に並ぶ） ----

const SWING = (p: number): number => 0.5 - Math.cos(p * Math.PI) / 2;
// jQuery UI 1.12.1 の速さの変わり方（前は /common/js/jquery-ui-easing.js）
const baseEasings: Record<string, (p: number) => number> = {};
['Quad', 'Cubic', 'Quart', 'Quint', 'Expo'].forEach((name, i) => {
  baseEasings[name] = (p) => Math.pow(p, i + 2);
});
Object.assign(baseEasings, {
  Sine: (p: number) => 1 - Math.cos((p * Math.PI) / 2),
  Circ: (p: number) => 1 - Math.sqrt(1 - p * p),
  Elastic: (p: number) => (p === 0 || p === 1 ? p : -Math.pow(2, 8 * (p - 1)) * Math.sin((((p - 1) * 80 - 7.5) * Math.PI) / 15)),
  Back: (p: number) => p * p * (3 * p - 2),
  Bounce: (p: number) => {
    let pow2, bounce = 4;
    while (p < ((pow2 = Math.pow(2, --bounce)) - 1) / 11) {
      /* 段を探す */
    }
    return 1 / Math.pow(4, 3 - bounce) - 7.5625 * Math.pow((pow2 * 3 - 2) / 22 - p, 2);
  },
});
export const EASING: Record<string, (p: number) => number> = { linear: (p) => p, swing: SWING };
for (const name in baseEasings) {
  const easeIn = baseEasings[name];
  EASING['easeIn' + name] = easeIn;
  EASING['easeOut' + name] = (p) => 1 - easeIn(1 - p);
  EASING['easeInOut' + name] = (p) => (p < 0.5 ? easeIn(p * 2) / 2 : 1 - easeIn(p * -2 + 2) / 2);
}
const SPEEDS: Record<string, number> = { slow: 600, fast: 200, _default: 400 };
const durationOf = (d: unknown): number => (typeof d === 'number' ? d : typeof d === 'string' && d in SPEEDS ? SPEEDS[d] : SPEEDS._default);

interface Tween {
  prop: string;
  start: number;
  end: number;
  unit: string;
  scroll: boolean;
}
interface Anim {
  el: El;
  tweens: Tween[];
  startTime: number;
  duration: number;
  easing: string;
  done: (() => void)[];
  stopped: boolean;
}
const queues = new WeakMap<Element, { running: boolean; list: (() => void)[] }>();
const running: Anim[] = [];
let frameTimer: Timer | null = null;

/** 動いている jQuery の代わりの動きの数（確かめる道具が「動きが終わるまで待つ」に使う。前は jQuery(':animated')） */
export const animatingCount = (): number => running.length;

function queueOf(el: El): { running: boolean; list: (() => void)[] } {
  let q = queues.get(el);
  if (!q) queues.set(el, (q = { running: false, list: [] }));
  return q;
}
function enqueue(el: El, fn: () => void): void {
  const q = queueOf(el);
  q.list.push(fn);
  if (!q.running) dequeue(el);
}
function dequeue(el: El): void {
  const q = queueOf(el);
  const fn = q.list.shift();
  if (!fn) {
    q.running = false;
    return;
  }
  q.running = true;
  fn();
}

function tick(): void {
  const now = Date.now();
  for (let i = 0; i < running.length; i++) {
    const a = running[i];
    const remaining = Math.max(0, a.startTime + a.duration - now);
    const percent = 1 - (remaining / a.duration || 0);
    for (const t of a.tweens) runTween(a, t, percent);
    if (!(percent < 1 && a.tweens.length)) {
      running.splice(i--, 1);
      finish(a);
    }
  }
  if (!running.length && frameTimer) {
    frameTimer.stop();
    frameTimer = null;
  }
}
function runTween(a: Anim, t: Tween, percent: number): void {
  const ease = EASING[a.easing] || SWING;
  const pos = a.duration ? ease(percent) : percent;
  const now = (t.end - t.start) * pos + t.start;
  if (t.scroll) {
    if (a.el.nodeType && a.el.parentNode) (a.el as unknown as Record<string, number>)[t.prop] = now;
  } else styleSet(a.el, t.prop, now + t.unit);
}
function finish(a: Anim): void {
  if (a.stopped) return;
  a.stopped = true;
  for (const fn of a.done) fn();
}
function startTimer(): void {
  if (!frameTimer) {
    frameTimer = new Timer();
    frameTimer.restart(tick);
  }
}

/**
 * 動かす（jQuery の animate。props は CSS の名前と終わりの値（数・"10px"・"show"/"hide"）、scrollTop・scrollLeft も）。
 * 同じ要素の動きは順番に並び、前の動きが終わってから始まる
 */
export function animate(t: Target, props: Record<string, number | string>, duration?: unknown, easing?: string, complete?: () => void): void {
  const dur = durationOf(duration);
  for (const el of els(t)) {
    enqueue(el, () => {
      const a: Anim = { el, tweens: [], startTime: Date.now(), duration: dur, easing: easing || 'swing', done: [], stopped: false };
      const showHideProps: Record<string, string> = {};
      let toShow: boolean | null = null;
      const hidden = isHiddenWithinTree(el);
      const orig: Record<string, string> = {};
      for (const p in props) {
        const v = props[p];
        if (v === 'show' || v === 'hide' || v === 'toggle') {
          const want = v === 'toggle' ? (hidden ? 'show' : 'hide') : v;
          if (want === (hidden ? 'hide' : 'show')) continue;
          showHideProps[p] = want;
          toShow = want === 'show';
          orig[p] = styleOf(el, p);
        } else {
          const prop = p;
          const scroll = prop === 'scrollTop' || prop === 'scrollLeft';
          const cur = scroll ? (el as unknown as Record<string, number>)[prop] : parseFloat(cssValue(el, prop, undefined, '')) || 0;
          const m = rcssNum.exec(String(v));
          const unit = scroll ? '' : (m && m[3]) || (CSS_NUMBER[camel(prop)] ? '' : 'px');
          let end = m ? +m[2] : +v;
          if (m && m[1]) end = cur + (m[1] + 1 === '+1' ? 1 : -1) * +m[2];
          a.tweens.push({ prop, start: cur, end, unit, scroll });
        }
      }
      // フェード（opacity の show・hide）: 隠れていれば先に出し、0 から今の値へ。消すときは今の値から 0 へ、終わったら隠して inline の値を元に戻す
      if (toShow !== null) {
        if (toShow) showHide([el], true);
        for (const p in showHideProps) {
          const cur = parseFloat(cssValue(el, p, undefined, '')) || 0;
          a.tweens.push(toShow ? { prop: p, start: 0, end: cur, unit: '', scroll: false } : { prop: p, start: cur, end: 0, unit: '', scroll: false });
        }
        a.done.push(() => {
          if (!toShow) showHide([el], false);
          for (const p in orig) styleSet(el, p, orig[p]);
        });
      }
      // 高さ・幅を動かす間ははみ出しを隠す（終わったら元に）
      if (('height' in props || 'width' in props) && el.nodeType === 1) {
        const ov = [el.style.overflow, el.style.overflowX, el.style.overflowY];
        el.style.overflow = 'hidden';
        a.done.unshift(() => {
          el.style.overflow = ov[0];
          el.style.overflowX = ov[1];
          el.style.overflowY = ov[2];
        });
      }
      a.done.push(() => {
        if (complete) complete.call(el);
        dequeue(el);
      });
      running.push(a);
      // ほかの動きが無ければ、すぐ最初の一コマ（始めの値。時間 0 ならその場で最後の形まで）。動いている間は次のコマから（jQuery と同じ）
      if (!frameTimer) {
        startTimer();
        tick();
      }
    });
  }
}
/** フェードで出す（jQuery の fadeIn。既定 400ms・swing） */
export const fadeIn = (t: Target, duration?: unknown, complete?: () => void): void => animate(t, { opacity: 'show' }, duration, undefined, complete);
/** フェードで消す（jQuery の fadeOut） */
export const fadeOut = (t: Target, duration?: unknown, complete?: () => void): void => animate(t, { opacity: 'hide' }, duration, undefined, complete);

/** 文書ができたら（jQuery の $(fn)。もうできていても、いまの処理のあとで呼ぶ） */
export function ready(fn: () => void): void {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setTimeout(fn), { once: true });
  else setTimeout(fn);
}

/** 次の動きまで待つ（jQuery の delay。同じ要素の動きの列に入る） */
export function delay(t: Target, ms: number): void {
  for (const el of els(t)) enqueue(el, () => setTimeout(() => dequeue(el), ms));
}

// ---- 文字・属性・値・中身（jQuery の text・attr・prop・val・append。値が undefined なら何もしない＝jQuery と同じ） ----

export function setText(t: Target, value: unknown): void {
  if (value === undefined) return;
  for (const el of els(t)) el.textContent = value == null ? '' : String(value);
}
export const text = (t: Target): string => {
  const list = els(t);
  return list.map((el) => el.textContent).join('');
};
/** 属性の値（無ければ undefined。jQuery の attr(名前)） */
export function attr(t: Target, name: string): string | undefined {
  const el = first(t);
  if (!el) return undefined;
  const v = el.getAttribute(name);
  return v == null ? undefined : v;
}
/** 属性を書く（null なら外す、undefined なら何もしない。jQuery の attr(名前, 値)） */
export function setAttr(t: Target, name: string, value: unknown): void {
  if (value === undefined) return;
  for (const el of els(t)) {
    if (value === null) el.removeAttribute(name);
    else el.setAttribute(name, value + '');
  }
}
export const removeAttr = (t: Target, name: string): void => {
  for (const el of els(t)) el.removeAttribute(name);
};
export const prop = (t: Target, name: string): unknown => {
  const el = first(t);
  return el ? (el as unknown as Record<string, unknown>)[name] : undefined;
};
export function setProp(t: Target, name: string, value: unknown): void {
  if (value === undefined) return;
  for (const el of els(t)) (el as unknown as Record<string, unknown>)[name] = value;
}
/** 入力欄の値（jQuery の val()。\r は外す） */
export function val(t: Target): string {
  const el = first(t) as HTMLInputElement | null;
  if (!el) return undefined as unknown as string;
  const v = el.value;
  return typeof v === 'string' ? v.replace(/\r/g, '') : v == null ? '' : v;
}
export function setVal(t: Target, value: unknown): void {
  if (value === undefined) return;
  for (const el of els(t)) (el as HTMLInputElement).value = value == null ? '' : String(value);
}
/** 中の最後に HTML を足す（jQuery の append(文字)） */
export function append(t: Target, html: string): void {
  for (const el of els(t)) el.insertAdjacentHTML('beforeend', html);
}
/** 子の要素（jQuery の children()） */
export const children = (t: Target): Element[] => els(t).reduce<Element[]>((a, el) => a.concat(Array.prototype.slice.call(el.children)), []);

// ---- イベント（jQuery の on・click(関数)・click()） ----

/**
 * 付ける（types は空白で区切った名前。fn の this は要素）。確かめる道具（check-classic-actions.mjs）が押せるものを見つけられるように、
 * クリック・押し始めなら要素に __charatOn（付けたイベントの名前の並び。dom.ts の onClick と同じ）を残す
 */
export function on<Ev extends Event = Event>(t: Target | Window | Document, types: string, fn: (this: Element, ev: Ev) => void): void {
  const list: (Element | Window | Document)[] = t === window || t === document ? [t as Window | Document] : els(t as Target);
  for (const el of list) {
    for (const type of types.trim().split(/\s+/)) {
      el.addEventListener(type, fn as EventListener);
      // 押せるものの印はクリック・押し始めだけ（scroll・mousewheel などは押せるものではない）
      if (type !== 'click' && type !== 'mousedown' && type !== 'touchstart') continue;
      const marked = el as unknown as { __charatOn?: string[] };
      marked.__charatOn = (marked.__charatOn || []).concat(type);
    }
  }
}
/** 押す（jQuery の click()。付けた処理とブラウザのクリック） */
export function click(t: Target): void {
  for (const el of els(t)) el.click();
}

/**
 * 文字を要素にする（jQuery の $("<…>") と同じ読み方: "<" で始まり ">" で終わる所を、文書の中の div の innerHTML で読み、直下のノードの並びにする。
 * タグ 1 つだけならその要素を作る。HTML でない文字はセレクター、文字でなければその要素）
 */
export function parseHTML(src: unknown): Node[] {
  if (typeof src !== 'string') return els(src as Target);
  let html: string | null = null;
  if (src[0] === '<' && src[src.length - 1] === '>' && src.length >= 3) html = src;
  else {
    const m = /^(?:\s*(<[\w\W]+>)[^>]*|#([\w-]+))$/.exec(src);
    if (m && m[1]) html = m[1];
    else if (m && m[2]) {
      const el = document.getElementById(m[2]);
      return el ? [el] : [];
    }
  }
  if (html === null) return els(src);
  const single = /^<([a-z][^\/\0>:\x20\t\r\n\f]*)[\x20\t\r\n\f]*\/?>(?:<\/\1>|)$/i.exec(html);
  if (single) return [document.createElement(single[1])];
  const div = document.createElement('div');
  div.innerHTML = html;
  return Array.prototype.slice.call(div.childNodes);
}

/**
 * 中の要素のクリックを外で受ける（jQuery の $(外).on(種類, セレクター, fn)。fn の this は押された所から外までで最初にセレクターに合う要素）。
 * 外の要素にも __charatOn の印（クリック・押し始めのとき）
 */
export function delegate(t: Target, types: string, selector: string, fn: (this: Element, ev: Event) => void): void {
  for (const root of els(t)) {
    on(root, types, function (ev: Event) {
      let el = ev.target as Element | null;
      while (el && el !== root) {
        if (el.nodeType === 1 && el.matches(selector)) {
          fn.call(el, ev);
          return;
        }
        el = el.parentNode as Element | null;
      }
    });
  }
}

/** 押せるものの印（__charatOn）だけを付ける（addEventListener で付けたクリック・押し始め。前は jQuery で付けていて、確かめる道具が jQuery の記録から見つけていたもの） */
export function markClickable(el: Element | null | undefined, types: string[]): void {
  if (!el) return;
  const marked = el as unknown as { __charatOn?: string[] };
  marked.__charatOn = (marked.__charatOn || []).concat(types.filter((t) => t === 'click' || t === 'mousedown' || t === 'touchstart'));
}
